import { FC, useContext, useMemo } from 'react';
import { merge } from 'lodash-es';
import { MdInfoOutline } from 'react-icons/md';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';

import {
  IconStateCheckFail,
  IconStateCheckIncomplete,
  IconStateCheckPass,
  IconStateCheckWarning,
} from '~/core/shared/assets';
import { Tooltip } from '~/core/shared/lib/chakraUi/components';
import { buildComponent } from '~/core/shared/utils/general';
import { SummaryTabContext } from '../../tabs';
import { ContentElementProps } from '../ContentElement.component';

type StateCheckConfig = {
  icons: Record<string, any>;
};

export const StateCheck: FC<ContentElementProps> = ({ element, ...props }) => {
  const { getElementConfig } = useContext(SummaryTabContext);

  const { value } = element;
  const colorMap: Record<string, string> = {
    pass: 'Green',
    warning: 'Yellow',
    fail: 'Red',
    incomplete: 'Gray',
  };

  const defaultConfig = useMemo(
    () =>
      ({
        icons: {
          fail: IconStateCheckFail,
          incomplete: IconStateCheckIncomplete,
          pass: IconStateCheckPass,
          warning: IconStateCheckWarning,
        },
      }) as StateCheckConfig,
    [],
  );

  const config = merge(defaultConfig, getElementConfig(element.format));

  const icon = buildComponent({
    component: config.icons[element.value],
  });

  const node = (
    <Flex
      border="1px solid"
      borderColor="gray.300"
      borderRadius={10}
      px={4}
      py={3}
      shadow="md"
      whiteSpace="nowrap"
    >
      <Box mr={2}>{icon}</Box>
      <Box>
        <Text color="gray.600" fontSize="12px" textTransform="uppercase">
          {element.name}
        </Text>
        <Box>
          <Text fontSize="20px" fontWeight="bold" textTransform="capitalize">
            {value}
          </Text>
        </Box>
      </Box>
      {element.options?.tooltips && (
        <Tooltip
          content={
            <ul>
              {Object.entries(element.options?.tooltips).map(([key, val]) => (
                <li key={key}>
                  <strong>{colorMap[key]}</strong>: {val}
                </li>
              ))}
            </ul>
          }
          contentProps={{
            maxW: '500px',
            width: 'inherit',
          }}
          showArrow
        >
          <Icon as={MdInfoOutline} ml={2} />
        </Tooltip>
      )}
    </Flex>
  );

  return <Box {...props}>{node}</Box>;
};
